import { useState, useEffect } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  TrendingDown,
  Minus,
  ShoppingCart,
  Zap,
  Car,
  Wifi,
  Home,
  BookOpen,
  Fuel,
  Calendar,
  Info,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  Calculator
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

export const PriceTrackerPage = () => {
  const { t, i18n } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('food');
  const [selectedCountry, setSelectedCountry] = useState(i18n.language === 'es' ? 'ES' : 'FR');
  const [timeframe, setTimeframe] = useState('12months');

  const categories = [
    {
      key: 'food',
      name: i18n.language === 'es' ? 'Alimentación' : 'Alimentation',
      icon: ShoppingCart,
      color: '#10b981'
    },
    {
      key: 'energy',
      name: i18n.language === 'es' ? 'Energía' : 'Énergie',
      icon: Zap,
      color: '#f59e0b'
    },
    {
      key: 'transport',
      name: i18n.language === 'es' ? 'Transporte' : 'Transport',
      icon: Car,
      color: '#3b82f6'
    },
    {
      key: 'telecom',
      name: i18n.language === 'es' ? 'Telecomunicaciones' : 'Télécommunications',
      icon: Wifi,
      color: '#8b5cf6'
    },
    {
      key: 'housing',
      name: i18n.language === 'es' ? 'Vivienda' : 'Logement',
      icon: Home,
      color: '#ef4444'
    }
  ];

  // Simulated price data (in real app, would come from INSEE/INE APIs)
  const generatePriceData = (category, country, months) => {
    const baseData = {
      FR: {
        food: { base: 100, trend: 0.3, volatility: 0.05 },
        energy: { base: 100, trend: 0.8, volatility: 0.12 },
        transport: { base: 100, trend: 0.4, volatility: 0.08 },
        telecom: { base: 100, trend: 0.1, volatility: 0.02 },
        housing: { base: 100, trend: 0.5, volatility: 0.03 }
      },
      ES: {
        food: { base: 100, trend: 0.4, volatility: 0.06 },
        energy: { base: 100, trend: 0.9, volatility: 0.15 },
        transport: { base: 100, trend: 0.5, volatility: 0.09 },
        telecom: { base: 100, trend: 0.2, volatility: 0.03 },
        housing: { base: 100, trend: 0.6, volatility: 0.04 }
      }
    };

    const config = baseData[country][category];
    const data = [];
    const now = new Date();

    for (let i = months - 1; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthProgress = (months - 1 - i) / (months - 1);
      const trend = config.base * (1 + config.trend * monthProgress);
      const noise = (Math.random() - 0.5) * config.volatility * config.base;
      const value = Math.round((trend + noise) * 100) / 100;
      
      data.push({
        month: date.toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'fr-FR', { 
          month: 'short', 
          year: '2-digit' 
        }),
        fullDate: date,
        value,
        change: i === months - 1 ? 0 : value - data[data.length - 1]?.value || 0
      });
    }

    return data;
  };

  const products = {
    FR: {
      food: ['Pain blanc (1kg)', 'Lait (1L)', 'Œufs (12)', 'Pommes (1kg)', 'Bœuf (1kg)'],
      energy: ['Électricité (kWh)', 'Gaz naturel (kWh)', 'Fioul domestique (L)', 'Pellets (kg)'],
      transport: ['Essence SP95 (L)', 'Gazole (L)', 'Ticket métro', 'Abonnement mensuel'],
      telecom: ['Internet ADSL', 'Fibre optique', 'Mobile 5Go', 'Mobile illimité'],
      housing: ['Loyer Paris (m²)', 'Charges copro', 'Taxe foncière', 'Assurance habitation']
    },
    ES: {
      food: ['Pan blanco (1kg)', 'Leche (1L)', 'Huevos (12)', 'Manzanas (1kg)', 'Ternera (1kg)'],
      energy: ['Electricidad (kWh)', 'Gas natural (kWh)', 'Gasóleo C (L)', 'Pellets (kg)'],
      transport: ['Gasolina 95 (L)', 'Gasóleo (L)', 'Metro Madrid', 'Abono mensual'],
      telecom: ['Internet ADSL', 'Fibra óptica', 'Móvil 5GB', 'Móvil ilimitado'],
      housing: ['Alquiler Madrid (m²)', 'Gastos comunidad', 'IBI', 'Seguro hogar']
    }
  };

  const [priceData, setPriceData] = useState([]);

  useEffect(() => {
    setSelectedCountry(i18n.language === 'es' ? 'ES' : 'FR');
  }, [i18n.language]);

  useEffect(() => {
    const months = timeframe === '6months' ? 6 : timeframe === '12months' ? 12 : 24;
    const data = generatePriceData(selectedCategory, selectedCountry, months);
    setPriceData(data);
  }, [selectedCategory, selectedCountry, timeframe]);

  const getLatestChange = () => {
    if (priceData.length < 2) return { change: 0, percentage: 0 };
    
    const latest = priceData[priceData.length - 1];
    const previous = priceData[priceData.length - 2];
    const change = latest.value - previous.value;
    const percentage = (change / previous.value) * 100;
    
    return { change, percentage };
  };

  const getOverallTrend = () => {
    if (priceData.length < 2) return { change: 0, percentage: 0 };
    
    const first = priceData[0];
    const latest = priceData[priceData.length - 1];
    const change = latest.value - first.value;
    const percentage = (change / first.value) * 100;
    
    return { change, percentage };
  };

  const getTrendIcon = (percentage) => {
    if (percentage > 2) return <TrendingUp className="w-4 h-4 text-red-500" />;
    if (percentage < -2) return <TrendingDown className="w-4 h-4 text-green-500" />;
    return <Minus className="w-4 h-4 text-gray-500" />;
  };

  const getTrendColor = (percentage) => {
    if (percentage > 2) return 'text-red-600';
    if (percentage < -2) return 'text-green-600';
    return 'text-gray-600';
  };

  const latestTrend = getLatestChange();
  const overallTrend = getOverallTrend();

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white">
      {/* Hero Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 
              className="text-4xl lg:text-5xl font-bold text-sage-900 mb-4"
              data-testid="price-tracker-title"
            >
              {t('tools.tracker.title')}
            </h1>
            <p className="text-xl text-sage-600 max-w-3xl mx-auto">
              {i18n.language === 'es'
                ? 'Sigue la evolución de precios en tiempo real para anticipar tus gastos y tomar mejores decisiones de compra.'
                : 'Suivez l\'évolution des prix en temps réel pour anticiper vos dépenses et prendre de meilleures décisions d\'achat.'
              }
            </p>
          </div>

          {/* Controls */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="border-sage-200 focus:border-sage-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => {
                    const IconComponent = category.icon;
                    return (
                      <SelectItem key={category.key} value={category.key}>
                        <div className="flex items-center gap-2">
                          <IconComponent className="w-4 h-4" style={{ color: category.color }} />
                          {category.name}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>

              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger className="border-sage-200 focus:border-sage-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FR">
                    <div className="flex items-center gap-2">
                      <span>🇫🇷</span>
                      France
                    </div>
                  </SelectItem>
                  <SelectItem value="ES">
                    <div className="flex items-center gap-2">
                      <span>🇪🇸</span>
                      España
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>

              <Select value={timeframe} onValueChange={setTimeframe}>
                <SelectTrigger className="border-sage-200 focus:border-sage-500">
                  <Calendar className="w-4 h-4 mr-2 text-sage-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6months">
                    {i18n.language === 'es' ? '6 meses' : '6 mois'}
                  </SelectItem>
                  <SelectItem value="12months">
                    {i18n.language === 'es' ? '12 meses' : '12 mois'}
                  </SelectItem>
                  <SelectItem value="24months">
                    {i18n.language === 'es' ? '24 meses' : '24 mois'}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-blue-600 mb-1">
                      {i18n.language === 'es' ? 'Tendencia general' : 'Tendance générale'}
                    </div>
                    <div className={`text-2xl font-bold ${getTrendColor(overallTrend.percentage)}`}>
                      {overallTrend.percentage > 0 ? '+' : ''}{overallTrend.percentage.toFixed(1)}%
                    </div>
                  </div>
                  {getTrendIcon(overallTrend.percentage)}
                </div>
                <div className="text-xs text-blue-600 mt-2">
                  {i18n.language === 'es' ? 'Desde' : 'Depuis'} {timeframe === '6months' ? '6 mois' : timeframe === '12months' ? '12 mois' : '24 mois'}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-purple-600 mb-1">
                      {i18n.language === 'es' ? 'Cambio mensual' : 'Évolution mensuelle'}
                    </div>
                    <div className={`text-2xl font-bold ${getTrendColor(latestTrend.percentage)}`}>
                      {latestTrend.percentage > 0 ? '+' : ''}{latestTrend.percentage.toFixed(1)}%
                    </div>
                  </div>
                  {getTrendIcon(latestTrend.percentage)}
                </div>
                <div className="text-xs text-purple-600 mt-2">
                  {i18n.language === 'es' ? 'Último mes' : 'Dernier mois'}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-green-600 mb-1">
                      {i18n.language === 'es' ? 'Índice base' : 'Indice de base'}
                    </div>
                    <div className="text-2xl font-bold text-green-700">
                      {priceData[priceData.length - 1]?.value.toFixed(1) || '100.0'}
                    </div>
                  </div>
                  <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-white" />
                  </div>
                </div>
                <div className="text-xs text-green-600 mt-2">
                  Base 100 = {i18n.language === 'es' ? 'Ene' : 'Jan'} 2023
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Chart */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {categories.find(c => c.key === selectedCategory)?.icon && 
                    React.createElement(categories.find(c => c.key === selectedCategory).icon, {
                      className: "w-5 h-5",
                      style: { color: categories.find(c => c.key === selectedCategory).color }
                    })
                  }
                  {i18n.language === 'es' ? 'Evolución de Precios' : 'Évolution des Prix'} - {categories.find(c => c.key === selectedCategory)?.name}
                </div>
                <Badge variant="outline">
                  {selectedCountry === 'FR' ? '🇫🇷 France' : '🇪🇸 España'}
                </Badge>
              </CardTitle>
              <CardDescription>
                {i18n.language === 'es' 
                  ? 'Índice de precios base 100 (enero 2023). Datos de fuentes oficiales actualizados mensualmente.'
                  : 'Indice de prix base 100 (janvier 2023). Données de sources officielles mises à jour mensuellement.'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={priceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="month" 
                    stroke="#6b7280"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="#6b7280"
                    fontSize={12}
                    domain={['dataMin - 2', 'dataMax + 2']}
                  />
                  <Tooltip 
                    formatter={(value) => [`${value.toFixed(1)}`, i18n.language === 'es' ? 'Índice' : 'Indice']}
                    labelStyle={{ color: '#374151' }}
                    contentStyle={{ 
                      backgroundColor: '#ffffff',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke={categories.find(c => c.key === selectedCategory)?.color || '#3b82f6'}
                    strokeWidth={3}
                    dot={{ fill: categories.find(c => c.key === selectedCategory)?.color, strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: categories.find(c => c.key === selectedCategory)?.color, strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Product Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Products List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-sage-600" />
                  {i18n.language === 'es' ? 'Productos Seguidos' : 'Produits Suivis'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {products[selectedCountry][selectedCategory].map((product, index) => {
                    // Simulate individual product trends
                    const baseTrend = overallTrend.percentage;
                    const productTrend = baseTrend + (Math.random() - 0.5) * 10;
                    
                    return (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <div className="font-medium text-sage-900">{product}</div>
                          <div className="text-sm text-sage-600">
                            {i18n.language === 'es' ? 'Fuente:' : 'Source:'} {selectedCountry === 'FR' ? 'INSEE' : 'INE'}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`font-bold ${getTrendColor(productTrend)}`}>
                            {productTrend > 0 ? '+' : ''}{productTrend.toFixed(1)}%
                          </div>
                          <div className="flex items-center gap-1">
                            {getTrendIcon(productTrend)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Analysis */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-sage-600" />
                  {i18n.language === 'es' ? 'Análisis de Tendencias' : 'Analyse des Tendances'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Key Insights */}
                <div className="space-y-3">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-medium text-blue-800 mb-2">
                      {i18n.language === 'es' ? 'Tendencia actual' : 'Tendance actuelle'}
                    </h4>
                    <p className="text-sm text-blue-700">
                      {overallTrend.percentage > 5 ? (
                        i18n.language === 'es' 
                          ? `Inflación significativa de +${overallTrend.percentage.toFixed(1)}% detectada. Considera alternativas de ahorro.`
                          : `Inflation significative de +${overallTrend.percentage.toFixed(1)}% détectée. Considérez des alternatives d'économie.`
                      ) : overallTrend.percentage > 0 ? (
                        i18n.language === 'es'
                          ? `Incremento moderado de +${overallTrend.percentage.toFixed(1)}%. Situación estable.`
                          : `Augmentation modérée de +${overallTrend.percentage.toFixed(1)}%. Situation stable.`
                      ) : (
                        i18n.language === 'es'
                          ? `Tendencia favorable de ${overallTrend.percentage.toFixed(1)}%. Buen momento para consumir.`
                          : `Tendance favorable de ${overallTrend.percentage.toFixed(1)}%. Bon moment pour consommer.`
                      )}
                    </p>
                  </div>

                  {Math.abs(overallTrend.percentage) > 5 && (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        {i18n.language === 'es' ? 'Recomendaciones' : 'Recommandations'}
                      </h4>
                      <ul className="text-sm text-amber-700 space-y-1">
                        <li>• {i18n.language === 'es' ? 'Usa nuestros comparateurs para encontrar mejores precios' : 'Utilisez nos comparateurs pour trouver de meilleurs prix'}</li>
                        <li>• {i18n.language === 'es' ? 'Considera cambiar de proveedor' : 'Envisagez de changer de fournisseur'}</li>
                        <li>• {i18n.language === 'es' ? 'Optimiza tu consumo con nuestras guías' : 'Optimisez votre consommation avec nos guides'}</li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <Separator />
                <div>
                  <h4 className="font-medium text-sage-800 mb-3">
                    {i18n.language === 'es' ? 'Acciones rápidas' : 'Actions rapides'}
                  </h4>
                  <div className="space-y-2">
                    <Button 
                      asChild 
                      variant="outline" 
                      className="w-full justify-start border-sage-200 text-sage-600 hover:bg-sage-50"
                    >
                      <a href={selectedCategory === 'energy' ? '/electricity' : selectedCategory === 'telecom' ? '/internet' : '#'}>
                        <Calculator className="w-4 h-4 mr-2" />
                        {i18n.language === 'es' ? 'Comparar proveedores' : 'Comparer fournisseurs'}
                      </a>
                    </Button>
                    
                    <Button 
                      asChild 
                      variant="outline" 
                      className="w-full justify-start border-sage-200 text-sage-600 hover:bg-sage-50"
                    >
                      <a href="/templates">
                        <TrendingUp className="w-4 h-4 mr-2" />
                        {i18n.language === 'es' ? 'Plantillas de negociación' : 'Templates de négociation'}
                      </a>
                    </Button>
                    
                    <Button 
                      asChild 
                      variant="outline" 
                      className="w-full justify-start border-sage-200 text-sage-600 hover:bg-sage-50"
                    >
                      <a href="/ebooks">
                        <BookOpen className="w-4 h-4 mr-2" />
                        {i18n.language === 'es' ? 'Guías de ahorro' : 'Guides d\'économie'}
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Data Sources */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg p-6 border">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-500" />
              {i18n.language === 'es' ? 'Fuentes de Datos' : 'Sources des Données'}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-700 mb-2">🇫🇷 France</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• <strong>INSEE:</strong> Institut national de la statistique</li>
                  <li>• <strong>Indices:</strong> {i18n.language === 'es' ? 'Precios al consumidor' : 'Prix à la consommation'}</li>
                  <li>• <strong>{i18n.language === 'es' ? 'Actualización:' : 'Mise à jour :'}</strong> {i18n.language === 'es' ? 'Mensual' : 'Mensuelle'}</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-700 mb-2">🇪🇸 España</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• <strong>INE:</strong> Instituto Nacional de Estadística</li>
                  <li>• <strong>IPC:</strong> {i18n.language === 'es' ? 'Índice de Precios al Consumo' : 'Indice des prix à la consommation'}</li>
                  <li>• <strong>{i18n.language === 'es' ? 'Actualización:' : 'Mise à jour :'}</strong> {i18n.language === 'es' ? 'Mensual' : 'Mensuelle'}</li>
                </ul>
              </div>
            </div>
            
            <p className="text-xs text-gray-500 mt-4">
              {i18n.language === 'es' 
                ? 'Los datos son indicativos y pueden presentar variaciones regionales. Para información oficial, consulte directamente las fuentes mencionadas.'
                : 'Les données sont indicatives et peuvent présenter des variations régionales. Pour informations officielles, consultez directement les sources mentionnées.'
              }
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};